import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCenters,
  setCentersLoading,
  setCentersError,
  setSelectedCenter,
  setCountry,
  clearCampData,
} from '../../../redux/slices/ykVisitSlice';
import ykVisitService from '../services/ykVisitService';
import './YkVisitCenter.css';

const YkVisitCenter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);

  const { centers, centersLoading, centersError, selectedCenter, country } =
    useSelector((state) => state.ykVisit);

  useEffect(() => {
    fetchCenters();
  }, [country]);

  const fetchCenters = async () => {
    if (fetchingRef.current) return;
    if (centers.length > 0) return;   // already cached
    fetchingRef.current = true;
    try {
      dispatch(setCentersLoading(true));
      const data = await ykVisitService.getCenters(country);
      dispatch(setCenters(data || []));
    } catch (err) {
      dispatch(setCentersError('Failed to load centers. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setCentersLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleCountryChange = (e) => {
    dispatch(setCountry(e.target.value));
    dispatch(setSelectedCenter(null));
    dispatch(clearCampData());
  };

  const handleSelectCenter = (center) => {
    dispatch(setSelectedCenter(center));
    dispatch(clearCampData());
    navigate(`/yk-visit/selection?centerId=${center.center_Id}&country=${country}`);
  };

  return (
    <div className="ykvisit-page">
      <div className="container-custom">

        {/* Header */}
        <div className="ykvisit-page-header">
          <div>
            <h1 className="ykvisit-page-title">Select Your Visit Center</h1>
            <p className="ykvisit-page-subtitle">Discover spiritual visit programs at our premium centers</p>
          </div>
        </div>

        {/* Loading State */}
        {centersLoading && (
          <div className="ykvisit-loading">
            <div className="inline-block">
              <div className="ykvisit-spinner"></div>
            </div>
            <p className="ykvisit-loading-text">Loading centers...</p>
          </div>
        )}

        {/* Error State */}
        {centersError && (
          <div className="ykvisit-error-banner">
            <p className="ykvisit-error-text">{centersError}</p>
            <button onClick={fetchCenters} className="ykvisit-error-retry">Try again</button>
          </div>
        )}

        {/* Centers Grid */}
        {!centersLoading && centers.length > 0 && (
          <div className="ykvisit-center-grid">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className={`item-card ${selectedCenter?.center_Id === center.center_Id ? 'selected' : ''}`}
                onClick={() => handleSelectCenter(center)}
              >
                {/* Image Section */}
                {center.center_Logo_URL && (
                  <div className="ykvisit-center-image-wrapper">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="ykvisit-center-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Visit+Center';
                      }}
                    />
                    <div className="ykvisit-center-image-overlay"></div>
                  </div>
                )}

                {/* Content Section */}
                <div className="ykvisit-center-body">
                  <h3 className="ykvisit-center-name">{center.center_Name}</h3>
                  <p className="ykvisit-center-desc">{center.center_desc}</p>
                  <button className="ykvisit-center-cta">Explore Visit Camps</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!centersLoading && centers.length === 0 && !centersError && (
          <div className="ykvisit-empty">
            <p className="ykvisit-empty-text">No centers available for the selected country.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default YkVisitCenter;
